import React, { useEffect, useState } from 'react'
import { Button } from '../../components/button'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { TextInput } from '../../components/inputs/textInput'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import { PageHeader } from '../../components/typography/PageHeader'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { useGetAllTransactionsQuery } from '../../redux-toolkits/transactions/transactions.slice'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import TransactionsTable from './TransactionsTable'

import { skipToken } from '@reduxjs/toolkit/query'
import { useSearchParams } from 'react-router-dom'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import { useAppSelector } from '../../hooks'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'

const TransactionsPage: React.FC = () => {
  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const [query, setQuery] = useState<QueryProps & { status?: string }>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const { data: transactions, isFetching: loadingTransactions } =
    useGetAllTransactionsQuery(
      query
        ? {
            fulfilmentCentre: selectedFulfilmentCenters
              .map((c) => c._id)
              .join(','),
            ...query,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const handlePageChange = (page: number): void => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: `${page}`,
    })
  }

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      status: searchParams.get('status') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
    })
  }, [searchParams])

  return (
    <Layout>
      <div className="flex flex-col md:items-center md:flex-row md:justify-between gap-4 mb-10">
        <PageHeader title={`Transactions`} className="capitalize !mb-0" />

        <div className="flex flex-col md:flex-row space-y-8 md:space-y-0">
          <DateFilterNav
            filter={query?.filter ?? 'custom'}
            isCustomDateRange={query?.isCustomDateRange ?? false}
            startDate={query?.startDate}
            endDate={query?.endDate}
            setFilter={(filter) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                filter: filter.filter ?? '',
                isCustomDateRange: String(filter.isCustomDateRange),
                endDate: filter.endDate ?? '',
                startDate: filter.startDate ?? '',
              })
            }}
          />

          <Button
            type="button"
            label="Export"
            primary={true}
            onClick={() => {
              handleModalOpen('exportDataModal')
            }}
            className="!w-full md:!max-w-[131px] md:ml-10"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                search: search,
                page: '1',
              })
            }}
            placeholder="Search reference"
            initialValue={searchParams.get('search') || undefined}
          />
        </div>

        <Dropdown
          menuClassName=""
          menuButton={
            <Button
              label={`Filter: ${query?.status ?? 'all'}`}
              primary={false}
              icon="filter"
              className="!font-normal ml-auto"
            />
          }
          menuItems={[
            { name: 'All', value: 'all' },
            { name: 'Completed', value: 'completed' },
            { name: 'Pending', value: 'pending' },
            { name: 'Failed', value: 'failed' },
            {
              name: 'Canceled',
              value: 'canceled',
            },
          ].map((filter) => ({
            ...filter,
            onClick: () => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                status: filter.value === 'all' ? '' : (filter.value as string),
                page: '1',
              })
            },
          }))}
        />
      </div>

      <div>
        <TransactionsTable
          transactionList={transactions?.data?.transactions || []}
          currentPage={(query?.page ?? 1) as number}
          totalDataCount={transactions?.data.totalCount ?? 0}
          pageLimit={(query?.limit ?? 20) as number}
          onPageChange={handlePageChange}
          loading={loadingTransactions}
        />
      </div>

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{
          transactionStatus: '',
        }}
        title="Export Transactions"
        reportType="transactions"
        fulfilmentCentre={selectedFulfilmentCenters.map((c) => c._id).join(',')}
      >
        {(form) => {
          return (
            <Dropdown
              menuClassName=""
              menuButton={
                <TextInput
                  type="text"
                  name="transactionStatus"
                  placeholder="Transaction status"
                  title="Transaction status"
                  value={form?.values?.transactionStatus || 'All'}
                  onBlur={form.handleBlur}
                  onChange={form.handleChange}
                  errors={''}
                  hasIcon={false}
                  extra={
                    <Icon
                      id={'left-caret'}
                      width={16}
                      height={16}
                      className="-rotate-90 text-sec-black"
                    />
                  }
                />
              }
              onClickMenuItem={(selected) => {
                form.setFieldValue(
                  'transactionStatus',
                  selected.value === 'all'
                    ? undefined
                    : (selected.value as string),
                )
              }}
              menuItems={[
                { name: 'All', value: 'all' },
                { name: 'Completed', value: 'completed' },
                { name: 'Pending', value: 'pending' },
                { name: 'Failed', value: 'failed' },
                {
                  name: 'Canceled',
                  value: 'canceled',
                },
              ]}
            />
          )
        }}
      </ExportDataModal>
    </Layout>
  )
}

export default TransactionsPage
